/**
 * The history preview — zoom out to a span, hold, and come back where you were.
 *
 * A chart opens on a hundred and fifty bars, which is where you read price and
 * not where you see context. The preview answers "what has this done for two
 * years" without asking anyone to zoom out, look, and zoom back to exactly the
 * window they had — that last step being the one nobody gets right by hand.
 *
 * ## It is a window animation, and nothing else
 *
 * Every frame is a `visibleBars` count handed to `applyWindow` anchored at the
 * **end**, so the right edge never moves and only the left one travels. That is
 * what makes it feel like the history arriving rather than the chart panning.
 *
 * Nothing here refetches. `ChartGroup.barsRange` decides what is loaded and the
 * preview only shows more of it — so the span is silently clamped to the
 * history in hand rather than triggering a request mid-animation, which would
 * stutter and, worse, land new bars while the window was moving over them.
 *
 * **Nothing here persists.** `applyWindow` writes to the chart builder and not
 * to the workspace, so a preview is view state in the strictest sense (D8): it
 * leaves no trace, and a reload shows the window the user actually chose. That
 * is also why it must finish exactly where it started — see below.
 *
 * ## Why it is pure, and why it returns a count rather than driving a clock
 *
 * `requestAnimationFrame` and the builder live in the component; this takes an
 * elapsed time and answers a count. So the whole shape of the motion — the
 * easing, the phase boundaries, the guarantee that the last frame is the first
 * frame — is testable in Node, where the chart is not.
 */

/** How long each phase of the preview lasts, in milliseconds. */
export interface PreviewTimings {
  /** Zooming out to the span. */
  outMs: number;
  /** Held at the full span, so there is something to actually look at. */
  holdMs: number;
  /** Coming back. Shorter than `outMs` on purpose: the way out is the content
   *  and the way back is a return, and a slow return reads as a wait. */
  backMs: number;
}

/**
 * The defaults, and the knobs a settings surface would expose.
 *
 * Named rather than inlined for exactly that reason — "preview 2 years over
 * 1.6s, hold 2s" is a preference, not a fact about charts, and the moment it is
 * configurable this is the shape the stored value takes.
 */
export const DEFAULT_TIMINGS: PreviewTimings = { outMs: 1600, holdMs: 2000, backMs: 1200 };

/** How far back the preview reaches, in years. */
export const DEFAULT_SPAN_YEARS = 2;

/** Total wall time for a preview, for a caller that needs to reason about it. */
export function previewDuration(timings: PreviewTimings = DEFAULT_TIMINGS): number {
  return timings.outMs + timings.holdMs + timings.backMs;
}

/**
 * Smooth at both ends, linear in the middle.
 *
 * Linear interpolation is visibly wrong here: the window would snap into motion
 * and stop dead, which on a chart reads as a glitch rather than as a movement.
 * Cubic in-out is the cheapest curve that starts and ends at rest.
 */
function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * How many bars should be visible `elapsed` ms into a preview.
 *
 * `null` means the preview is over — and the frame before it is exactly `from`,
 * not merely close to it. That is the property the whole feature rests on: a
 * preview that returned to 149 bars instead of 150 would quietly edit the
 * user's chart every time they looked at the history.
 *
 * Rounded per frame rather than at the end, because `setBarWindow` takes a bar
 * index either way and a fractional count would round somewhere less visible.
 */
export function previewFrame(
  from: number,
  to: number,
  elapsed: number,
  timings: PreviewTimings = DEFAULT_TIMINGS,
): number | null {
  if (elapsed < 0) return from;
  const { outMs, holdMs, backMs } = timings;

  if (elapsed < outMs) {
    return Math.round(from + (to - from) * ease(outMs === 0 ? 1 : elapsed / outMs));
  }
  if (elapsed < outMs + holdMs) return to;
  if (elapsed < outMs + holdMs + backMs) {
    const t = backMs === 0 ? 1 : (elapsed - outMs - holdMs) / backMs;
    return Math.round(to + (from - to) * ease(t));
  }
  return null;
}

/**
 * The bar count a `years`-long preview should reach, or `undefined` for none.
 *
 * Measured from the loaded timestamps rather than modelled from the calendar,
 * for `chart-window.barsBackTo`'s reason: a session model has to know the
 * exchange's hours, its holidays and its half-days, and is wrong about every
 * one it does not. The bars in hand already answer it.
 *
 * `undefined` when the target is not actually more than what is on screen —
 * a "preview" that zoomed *in*, or did nothing, is worse than a disabled
 * button, because it looks like the feature is broken rather than unavailable.
 */
export function previewTarget(
  x: readonly number[],
  visibleBars: number,
  years: number = DEFAULT_SPAN_YEARS,
  now: Date = new Date(),
): number | undefined {
  if (x.length === 0) return undefined;

  // UTC, like the bars — see `chart-window.spanStart`. A boundary computed in
  // the reader's local time is offset from the data by their whole timezone.
  const startSec =
    Date.UTC(now.getUTCFullYear() - years, now.getUTCMonth(), now.getUTCDate()) / 1000;

  const first = x[0]!;
  // Clamped to what is loaded rather than refetching: the preview shows history
  // already in hand, and a request mid-animation would stutter and land new
  // bars under a moving window.
  const target = first > startSec ? x.length : countFrom(x, startSec);
  return target > visibleBars ? Math.min(target, x.length) : undefined;
}

/** Bars from the first one at or after `startSec` to the end, inclusive. */
function countFrom(x: readonly number[], startSec: number): number {
  let lo = 0;
  let hi = x.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x[mid]! < startSec) lo = mid + 1;
    else hi = mid;
  }
  return Math.max(1, x.length - lo);
}
